import type {ChangeStyleTheme, FeedbackType} from '@Components/ai-chat-window/ai-chat-window.types';

export interface FeedbackPopupProps {
  idAiChat: string;
  messageId?: string;
  onSubmitFeedback: (type: FeedbackType, negativeFeedbackPreset: AIChatNegativeFeedbackTypes | null, text: string | null) => void;
  isAlreadySubmitted?: boolean;
  initialNegativeFeedbackPreset?: AIChatNegativeFeedbackTypes | null;
  onChangeStyleThemeSelect: (theme: ChangeStyleTheme) => void;
  onClickTemplate: VoidFunction;
  onClickEditInEditor: VoidFunction;
  isMessageStreaming?: boolean;
  canWrite?: boolean;
}

export enum SuggestionActionKey {
  CHANGE_STYLE = '{{changeStyle}}',
  TEMPLATE = '{{template}}',
  EDIT_IN_EDITOR = '{{editInEditor}}',
}

export enum AIChatNegativeFeedbackTypes {
  DIDNT_FOLLOW_INSTRUCTIONS = 'didnt_follow_instructions',
  TEXT_OR_SPELLING_ERRORS = 'spelling_errors',
  DONT_LIKE_DESIGN = 'dont_like_design',
  OTHER = 'other',
}

export interface NegativeFeedbackSuccessPanel {
  subtext: string;
  sugesstions: NegativeFeedbackSuggestion[];
}

export enum SuggestionPointTypes {
  STRING = 'text',
  ACTIONBLE = 'actionable',
}

export type SuggestionPointType = {type: SuggestionPointTypes.STRING; content: string} | {type: SuggestionPointTypes.ACTIONBLE; content: string; actionKey: SuggestionActionKey};

export interface NegativeFeedbackSuggestion {
  icon: string;
  heading: string;
  points: SuggestionPointType[];
}

export const getSuggestions = (feedbackPreset: AIChatNegativeFeedbackTypes): NegativeFeedbackSuccessPanel => {
  return getFeedbackSuggestionsMap()[feedbackPreset];
};

export const getFeedbackSuggestionsMap = (): Record<AIChatNegativeFeedbackTypes, NegativeFeedbackSuccessPanel> => {
  return {
    [AIChatNegativeFeedbackTypes.DIDNT_FOLLOW_INSTRUCTIONS]: {
      subtext: window.i18next.t('pmwjs_while_we_work_on_improving_this_you_can_try'),
      sugesstions: [
        {
          icon: 'icon-pencil',
          heading: window.i18next.t('pmwjs_if_an_edit_is_missed'),
          points: [
            {type: SuggestionPointTypes.STRING, content: window.i18next.t('pmwjs_request_the_edit_again_in_a_new_chat_with_the_previous_design_attached')},
            {type: SuggestionPointTypes.STRING, content: window.i18next.t('pmwjs_start_a_new_chat_context_can_fade_in_long_chats')},
            {type: SuggestionPointTypes.STRING, content: window.i18next.t('pmwjs_ai_isnt_very_reliable_at_aligning_or_moving_elements')},
          ],
        },
        {
          icon: 'icon-message-prompt',
          heading: window.i18next.t('pmwjs_make_prompts_specific'),
          points: [
            {type: SuggestionPointTypes.STRING, content: window.i18next.t('pmwjs_attach_a_screenshot_of_the_element_you_want_to_fix')},
            {type: SuggestionPointTypes.STRING, content: window.i18next.t('pmwjs_specify_object_location_fix_logo_in_the_top_right')},
            {type: SuggestionPointTypes.STRING, content: window.i18next.t('pmwjs_describe_how_it_looks_fix_the_red_and_green_border')},
            {type: SuggestionPointTypes.STRING, content: window.i18next.t('pmwjs_add_exact_text_in_quotes_event_starts_today')},
          ],
        },
      ],
    },
    [AIChatNegativeFeedbackTypes.TEXT_OR_SPELLING_ERRORS]: {
      subtext: window.i18next.t('pmwjs_spelling_errors_can_happen_you_can_try'),
      sugesstions: [
        {
          icon: 'icon-text',
          heading: window.i18next.t('pmwjs_for_text_edits'),
          points: [
            {type: SuggestionPointTypes.STRING, content: window.i18next.t('pmwjs_be_specific_when_editing_text_change_summer_gala_to_winter_sale')},
            {type: SuggestionPointTypes.STRING, content: window.i18next.t('pmwjs_put_exact_text_in_quotes_add_limited_time_offer')},
            {type: SuggestionPointTypes.STRING, content: window.i18next.t('pmwjs_remove_unwanted_text_by_saying_remove_grand_opening')},
          ],
        },
        {
          icon: 'icon-spellcheck',
          heading: window.i18next.t('pmwjs_for_spelling_issues'),
          points: [
            {type: SuggestionPointTypes.STRING, content: window.i18next.t('pmwjs_start_a_new_chat_ai_makes_less_errors_when_starting_fresh')},
            {
              type: SuggestionPointTypes.ACTIONBLE,
              content: window.i18next.t('pmwjs_use_ai_to_remove_unwanted_text_and_click_on_edit_in_editor_to_customize_further', {editInEditor: SuggestionActionKey.EDIT_IN_EDITOR}),
              actionKey: SuggestionActionKey.EDIT_IN_EDITOR,
            },
          ],
        },
      ],
    },
    [AIChatNegativeFeedbackTypes.DONT_LIKE_DESIGN]: {
      subtext: window.i18next.t('pmwjs_while_we_work_on_improving_this_you_can_try'),
      sugesstions: [
        {
          icon: 'icon-repeat',
          heading: window.i18next.t('pmwjs_to_restyle_your_design'),
          points: [
            {
              type: SuggestionPointTypes.ACTIONBLE,
              content: window.i18next.t('pmwjs_click_on_change_style_to_completely_restyle_your_design', {changeStyle: SuggestionActionKey.CHANGE_STYLE}),
              actionKey: SuggestionActionKey.CHANGE_STYLE,
            },
            {
              type: SuggestionPointTypes.ACTIONBLE,
              content: window.i18next.t('pmwjs_attach_a_template_in_your_prompt_to_guide_the_look', {template: SuggestionActionKey.TEMPLATE}),
              actionKey: SuggestionActionKey.TEMPLATE,
            },
            {type: SuggestionPointTypes.STRING, content: window.i18next.t('pmwjs_start_a_new_chat_ai_explores_new_ideas_when_starting_fresh')},
            {type: SuggestionPointTypes.STRING, content: window.i18next.t('pmwjs_mention_the_style_you_want_in_your_prompt_minimalist_elegant_retro')},
          ],
        },
      ],
    },
    [AIChatNegativeFeedbackTypes.OTHER]: {
      subtext: window.i18next.t('pmwjs_your_feedback_helps_us_improve'),
      sugesstions: [],
    },
  };
};

export const FEEDBACK_COMMENT_MAX_LENGTH = 200;
